import { useCallback, useEffect, useState } from "react";
import { fetchVehicleOptions } from "../services/maintenanceService";
import type { VehicleOption } from "../types";

// Las unidades de la compañía (con su foto, placa y tipo) se comparten entre
// secciones durante un minuto: cambiar de sección no vuelve a pedir
// /vehicleList, pero cambiar de compañía o encender el Sandbox se ve pronto.
const TTL_MS = 60 * 1000;
let cache: VehicleOption[] | null = null;
let cachedAt = 0;
let inflight: Promise<VehicleOption[]> | null = null;

function loadVehicles(): Promise<VehicleOption[]> {
  if (cache && Date.now() - cachedAt < TTL_MS) return Promise.resolve(cache);
  if (!inflight) {
    inflight = fetchVehicleOptions()
      .then((list) => {
        cache = list;
        cachedAt = Date.now();
        return list;
      })
      .catch(() => [] as VehicleOption[])
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
}

/** Sólo para pruebas: olvida la caché. */
export function resetVehiclesCache() {
  cache = null;
  cachedAt = 0;
  inflight = null;
}

export function useVehicles() {
  const [vehicles, setVehicles] = useState<VehicleOption[]>(cache ?? []);
  const [loading, setLoading] = useState(!cache);

  useEffect(() => {
    let alive = true;
    loadVehicles().then((list) => {
      if (!alive) return;
      setVehicles(list);
      setLoading(false);
    });
    return () => {
      alive = false;
    };
  }, []);

  const vehicleOf = useCallback(
    (id?: string | null) => vehicles.find((v) => v.id === id) ?? null,
    [vehicles]
  );
  const labelOf = useCallback(
    (id?: string | null) => vehicleOf(id)?.label ?? id ?? "—",
    [vehicleOf]
  );
  const imageOf = useCallback((id?: string | null) => vehicleOf(id)?.image ?? null, [vehicleOf]);

  return { vehicles, loading, labelOf, vehicleOf, imageOf };
}
